import { Link } from '@remix-run/react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { Image as ImageType } from 'types/capi/shared';

import MailIcon from '~/assets/icons/Mail';
import VectorIcon from '~/assets/icons/Vector';
import { isInAppBrowser } from '~/utils/browser';

import { DialogDismiss } from '../../Dialog';
import Image, { smallImageOptions } from '../../Image';
import LoginButtons from '../../LoginButtons';
import Tracked from '../../Tracked';
import type { LoginModalType, ScreenProps } from '.';

function getModalHeadingsText(
  modalType: LoginModalType,
  t: TFunction<'common', undefined>
) {
  return {
    notifications: {
      description: t('login.stageInitial.notificationAttempt.description'),
      title: t('login.stageInitial.notificationAttempt.title')
    },
    pdpGate: {
      description: t('login.stageInitial.pdpGate.description'),
      title: t('login.stageInitial.pdpGate.title')
    },
    profile: {
      description: t('login.stageInitial.profilePage.description'),
      title: t('login.stageInitial.profilePage.title')
    },
    purchase: {
      description: '',
      title: 'Sign in to buy these sneakers'
    },
    raffle: {
      description: t('login.stageInitial.raffle.description'),
      title: t('login.stageInitial.raffle.title')
    }
  }[modalType];
}

const ModalHeadings = ({
  modalType,
  image
}: {
  image?: ImageType;
  modalType: LoginModalType;
}) => {
  const { t } = useTranslation('common');

  const { title, description } = getModalHeadingsText(modalType, t);

  return (
    <>
      <div className="flex flex-row justify-center">
        <h2 className="mx-auto my-0 text-center font-bold text-black">
          {title}
        </h2>
        {modalType === 'profile' ? (
          <Link to="/">
            <VectorIcon width="10" height="10" />
          </Link>
        ) : (
          <DialogDismiss className="text-black">
            <VectorIcon width="10" height="10" />
          </DialogDismiss>
        )}
      </div>
      {image && (
        <div className="my-4">
          <Image
            className="mx-auto aspect-video w-48 object-cover align-middle"
            loading="eager"
            src={image.url}
            alt={image.alt}
            {...smallImageOptions({ pad: true })}
          />
        </div>
      )}
      <h3 className="pb-4 text-center font-normal text-black">{description}</h3>
    </>
  );
};

export default function InitialScreen({
  setScreen,
  onDismiss,
  postAuthOptions,
  onError,
  image,
  modalType
}: ScreenProps) {
  const { t } = useTranslation();

  const isInApp = isInAppBrowser(navigator.userAgent);

  return (
    <>
      <ModalHeadings modalType={modalType} image={image} />
      {!isInApp && (
        <div className="flex items-center justify-center gap-4 pb-4">
          <LoginButtons.Google
            pagePosition="Sign in modal"
            postAuthOptions={postAuthOptions}
            onSuccess={() => onDismiss({ loginSuccessful: true })}
            onError={onError}
          />

          <LoginButtons.Facebook
            pagePosition="Sign in modal"
            postAuthOptions={postAuthOptions}
            onSuccess={() => onDismiss({ loginSuccessful: true })}
            onError={onError}
          />

          <LoginButtons.Apple
            pagePosition="Sign in modal"
            postAuthOptions={postAuthOptions}
            onSuccess={() => onDismiss({ loginSuccessful: true })}
            onError={onError}
          />
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-4 md:flex-row">
        <Tracked.Button
          className="h-12 w-full px-2"
          variant="forest"
          onClick={() => setScreen('signUp')}
          name="SIGN_UP_EMAIL_PROMPT"
          pagePosition={'Sign in modal'}
        >
          <div className="flex items-center justify-center gap-2">
            <MailIcon height="18" width="18" stroke="white" />
            <p>{t('login.stageInitial.emailSignUpButton')}</p>
          </div>
        </Tracked.Button>
        <Tracked.Button
          className="h-12 w-full px-2.5"
          onClick={() => setScreen('signIn')}
          variant="transparent-black-border"
          name="EXISTING_USER_LOGIN_PROMPT"
          pagePosition={'Sign in modal'}
        >
          <div className="flex items-center justify-center gap-2">
            <MailIcon height="18" width="18" stroke="black" />
            <p>{t('login.stageInitial.emailLoginButton')}</p>
          </div>
        </Tracked.Button>
      </div>
    </>
  );
}
